import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Progress } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { bgAuthImg, logo } from "@/asset/img";
import { useGetCompanyQuery } from "@/services/api/company/companyApi";
import useAuth from "@/hooks/useAuth";
import { useChangePasswordApiMutation } from "@/services/api/auth/authApi";

const ChangePassword = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const user = useAuth();
  const { token } = useParams(); // Extract token from URL params
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  //   const [activateAccount, { isLoading: isActivatingAccount }] =
  //     useAccountActivationMutation();
  const [changePassword, { isLoading: isRessetting }] =
    useChangePasswordApiMutation();
  const [password, setPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
    percentage: 0,
  });
  const { data: companyData } = useGetCompanyQuery();
  const companyLogo = companyData?.[0]?.url;

  // Check if token exists on mount
  useEffect(() => {
    if (!token) {
      message.error("Invalid or missing activation token");
      navigate("/admin-auth");
    }
  }, [token, navigate]);

  // Calculate password strength
  const calculatePasswordStrength = (pwd) => {
    let score = 0;
    const checks = {
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasLowerCase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
    };

    // Calculate score
    Object.values(checks).forEach((check) => {
      if (check) score++;
    });

    // Determine strength level
    let strength = {
      score: 0,
      label: "",
      color: "",
      percentage: 0,
    };

    if (score === 0) {
      strength = { score: 0, label: "", color: "", percentage: 0 };
    } else if (score <= 2) {
      strength = { score: 1, label: "Weak", color: "#ff4d4f", percentage: 33 };
    } else if (score <= 4) {
      strength = {
        score: 2,
        label: "Medium",
        color: "#faad14",
        percentage: 66,
      };
    } else {
      strength = {
        score: 3,
        label: "Strong",
        color: "#52c41a",
        percentage: 100,
      };
    }

    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    calculatePasswordStrength(pwd);
  };

  const handleSubmit = async (values) => {
    // Prevent submission if password is weak
    if (passwordStrength.score < 2) {
      message.warning("Please create a stronger password");
      return;
    }

    try {
      await changePassword({
        email: user?.email,
        old_password: values.old_password,
        password: values.new_password,
      }).unwrap();

      message.success("Password changed successfully!");
      setTimeout(() => {
        navigate("/admin-auth");
        form.resetFields();
        setPassword("");
        setPasswordStrength({ score: 0, label: "", color: "", percentage: 0 });
      }, 1000);
    } catch (error) {
      message.error(
        error?.data?.message || "Failed to change password. Please try again.",
      );
    }
  };

  return (
    <div className="min-h-screen flex p-6">
      {/* Left Side - Hero Section */}
      <div className="hidden lg:flex lg:w-1/2 bg-black relative overflow-hidden rounded-3xl">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={bgAuthImg}
            alt="Delicious Food"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content Container with proper margins */}
        <div className="relative z-10 flex flex-col justify-between w-full m-6">
          {/* Inner container with rounded corners and proper padding */}
          <div className="bg-opacity-30  rounded-3xl p-8 h-full flex flex-col justify-between">
            {/* Logo */}
            <div className="mb-8">
              <div className="w-24 h-24 rounded-full flex items-center justify-center">
                <img src={companyLogo} alt="Chef Zee" />
              </div>
            </div>

            {/* Hero Text */}
            <div className="flex-1 flex flex-col justify-center">
              <h1 className="text-4xl font-bold leading-tight mb-4 text-white">
                Delicious Food,
                <br />
                Delivered in Bulk.
              </h1>
              <p className="text-[#E4DBDB] text-sm max-w-md leading-relaxed">
                Order premium quality meals for your team or event. From Office
                lunches to full-service catering, we've got you covered.
              </p>
            </div>

            {/* Testimonial Card */}
          </div>
        </div>
      </div>

      {/* Right Side - Change Password Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Change Password
            </h2>
            <p className="text-gray-600 text-sm">
              Create a strong password to secure your account
            </p>
          </div>

          {/* Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
          >
            {/* Password Field */}
            <Form.Item
              label={
                <span className="text-gray-700 font-medium">Old Password</span>
              }
              name="old_password"
              rules={[
                { required: true, message: "Please enter your password" },
              ]}
              validateStatus={
                password && passwordStrength.score < 2 ? "warning" : ""
              }
            >
              <Input.Password
                placeholder="Enter old password"
                className="h-12 rounded-full px-6"
                onChange={handlePasswordChange}
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
                visibilityToggle={{
                  visible: showPassword,
                  onVisibleChange: setShowPassword,
                }}
              />
            </Form.Item>

            {/* Password Strength Indicator - Simple and Clean */}
            {password && (
              <div className="mb-6 -mt-4">
                <Progress
                  percent={passwordStrength.percentage}
                  strokeColor={passwordStrength.color}
                  showInfo={false}
                  strokeWidth={6}
                  className="mb-3"
                />

                {/* Simple emoji + text feedback */}
                {passwordStrength.score > 0 && (
                  <div className="flex items-center gap-2">
                    {passwordStrength.score === 1 && (
                      <>
                        <span className="text-xl">😕</span>
                        <span className="text-sm text-gray-600">
                          Weak: Add uppercase, lowercase, numbers & special
                          characters
                        </span>
                      </>
                    )}
                    {passwordStrength.score === 2 && (
                      <>
                        <span className="text-xl">😐</span>
                        <span className="text-sm text-gray-600">
                          Medium: Try adding special characters (!@#$%) for a
                          stronger password
                        </span>
                      </>
                    )}
                    {passwordStrength.score === 3 && (
                      <>
                        <span className="text-xl">😊</span>
                        <span className="text-sm text-green-600 font-medium">
                          Strong: Great password!
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Confirm Password Field */}
            <Form.Item
              label={
                <span className="text-gray-700 font-medium">New Password</span>
              }
              name="new_password"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please confirm your password" },
              ]}
            >
              <Input.Password
                placeholder="Enter new password"
                className="h-12 rounded-full px-6"
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
                visibilityToggle={{
                  visible: showConfirmPassword,
                  onVisibleChange: setShowConfirmPassword,
                }}
              />
            </Form.Item>

            {/* Submit Button */}
            <Form.Item className="mb-4">
              <Button
                type="primary"
                htmlType="submit"
                loading={isRessetting}
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 border-none rounded-full text-white font-medium text-base shadow-lg"
              >
                Change Password
              </Button>
            </Form.Item>

            {/* Back to Login */}
            <div className="text-center">
              <Link
                to="/admin-auth"
                className="text-gray-600 hover:text-orange-500 text-sm transition-colors"
              >
                ← Back to Login
              </Link>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
